import type { jsPDF } from 'jspdf';

/**
 * 사각형 그리기 옵션
 */
export interface TypeDrawRectOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  fillColor?: string; // hex 색상 (#RRGGBB)
  borderColor?: string;
  borderWidth?: number;
}

/**
 * 텍스트 그리기 옵션
 */
export interface TypeDrawTextOptions {
  x: number;
  y: number;
  text: string;
  fontSize?: number;
  fontStyle?: 'normal' | 'bold';
  align?: 'left' | 'center' | 'right';
  maxWidth?: number;
}

/**
 * 테이블 셀 그리기 옵션 (사각형 + 텍스트)
 */
export interface TypeDrawTableCellOptions extends TypeDrawRectOptions, Omit<TypeDrawTextOptions, 'x' | 'y'> {
  textOffsetX?: number; // 셀 내부 텍스트 X 오프셋
  textOffsetY?: number; // 셀 내부 텍스트 Y 오프셋
}

/**
 * 선 그리기 옵션
 */
export interface TypeDrawLineOptions {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color?: string;
  width?: number;
}

/**
 * hex 색상을 RGB로 변환
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

/**
 * 사각형 그리기
 */
export const drawRect = (pdf: jsPDF, options: TypeDrawRectOptions): void => {
  const { x, y, width, height, fillColor, borderColor = '#000000', borderWidth = 0.5 } = options;

  // 배경색 채우기
  if (fillColor) {
    const rgb = hexToRgb(fillColor);
    pdf.setFillColor(rgb.r, rgb.g, rgb.b);
    pdf.rect(x, y, width, height, 'F');
  }

  // 테두리 그리기
  if (borderWidth > 0) {
    const borderRgb = hexToRgb(borderColor);
    pdf.setDrawColor(borderRgb.r, borderRgb.g, borderRgb.b);
    pdf.setLineWidth(borderWidth);
    pdf.rect(x, y, width, height, 'S');
  }
};

/**
 * 텍스트 그리기
 */
export const drawText = (pdf: jsPDF, options: TypeDrawTextOptions): void => {
  const { x, y, text, fontSize = 12, fontStyle = 'normal', align = 'left', maxWidth } = options;

  // 폰트 설정
  pdf.setFontSize(fontSize);
  if (fontStyle === 'bold') {
    pdf.setFont('NanumGothic', 'bold');
  } else {
    pdf.setFont('NanumGothic', 'normal');
  }
  pdf.setTextColor(0, 0, 0);

  // 정렬 설정
  const alignMap: Record<string, 'left' | 'center' | 'right'> = {
    left: 'left',
    center: 'center',
    right: 'right',
  };

  // 텍스트 그리기
  if (maxWidth) {
    pdf.text(text, x, y, { align: alignMap[align], maxWidth });
  } else {
    pdf.text(text, x, y, { align: alignMap[align] });
  }
};

/**
 * 선 그리기
 */
export const drawLine = (pdf: jsPDF, options: TypeDrawLineOptions): void => {
  const { x1, y1, x2, y2, color = '#000000', width = 0.5 } = options;

  const rgb = hexToRgb(color);
  pdf.setDrawColor(rgb.r, rgb.g, rgb.b);
  pdf.setLineWidth(width);
  pdf.line(x1, y1, x2, y2);
};

/**
 * 테이블 셀 그리기 (배경 + 테두리 + 텍스트)
 */
export const drawTableCell = (pdf: jsPDF, options: TypeDrawTableCellOptions): void => {
  const {
    x,
    y,
    width,
    height,
    fillColor,
    borderColor,
    borderWidth,
    text,
    fontSize,
    fontStyle,
    align = 'left',
    textOffsetX = 2,
    textOffsetY,
    maxWidth,
  } = options;

  // 사각형 그리기
  drawRect(pdf, { x, y, width, height, fillColor, borderColor, borderWidth });

  // 텍스트 그리기
  if (text) {
    // 수평 정렬에 따른 X 좌표 계산
    let textX = x;
    if (align === 'left') {
      textX += textOffsetX;
    } else if (align === 'center') {
      textX += width / 2;
    }

    // 수직 정렬에 따른 Y 좌표 계산
    const actualFontSize = fontSize || 12;
    let textY = y + height / 2;

    if (textOffsetY !== undefined) {
      textY += textOffsetY;
    } else {
      textY += actualFontSize * 0.1;
    }

    drawText(pdf, {
      x: textX,
      y: textY,
      text,
      fontSize,
      fontStyle,
      align,
      maxWidth: maxWidth || width - textOffsetX * 2,
    });
  }
};
